/**
 * The mapping between model millimetres and canvas pixels.
 *
 * Model space has y pointing down, the same as the canvas, so the transform is
 * a uniform scale plus a translation -- no flip anywhere.
 */

import type { Point } from '../model/types';

export interface Viewport {
  /** Model millimetres at the canvas origin. */
  originX: number;
  originY: number;
  /** Canvas pixels per model millimetre. */
  zoom: number;
}

/** 1 px per 20 mm shows a 12 m wide flat in a 600 px canvas. */
export const DEFAULT_VIEWPORT: Viewport = { originX: -1000, originY: -1000, zoom: 0.05 };

const MIN_ZOOM = 0.002;
const MAX_ZOOM = 2;

export function toScreen(viewport: Viewport, point: Point): Point {
  return {
    x: (point.x - viewport.originX) * viewport.zoom,
    y: (point.y - viewport.originY) * viewport.zoom,
  };
}

export function toModel(viewport: Viewport, point: Point): Point {
  return {
    x: point.x / viewport.zoom + viewport.originX,
    y: point.y / viewport.zoom + viewport.originY,
  };
}

/** Converts a pixel distance to millimetres, for hit-test tolerances. */
export function pixelsToMm(viewport: Viewport, pixels: number): number {
  return pixels / viewport.zoom;
}

export function pan(viewport: Viewport, dxPixels: number, dyPixels: number): Viewport {
  return {
    ...viewport,
    originX: viewport.originX - dxPixels / viewport.zoom,
    originY: viewport.originY - dyPixels / viewport.zoom,
  };
}

/** Zooms about a canvas point, so whatever is under the cursor stays there. */
export function zoomAt(viewport: Viewport, screenPoint: Point, factor: number): Viewport {
  const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, viewport.zoom * factor));
  if (zoom === viewport.zoom) return viewport;

  const anchor = toModel(viewport, screenPoint);
  return {
    zoom,
    originX: anchor.x - screenPoint.x / zoom,
    originY: anchor.y - screenPoint.y / zoom,
  };
}

/** Frames a bounding box with a margin, used by "zoom to fit". */
export function fitTo(
  bounds: { minX: number; minY: number; maxX: number; maxY: number } | null,
  canvasWidth: number,
  canvasHeight: number,
): Viewport {
  if (!bounds || canvasWidth === 0 || canvasHeight === 0) return DEFAULT_VIEWPORT;

  const width = Math.max(bounds.maxX - bounds.minX, 1000);
  const height = Math.max(bounds.maxY - bounds.minY, 1000);
  const margin = 1.15;
  const zoom = Math.min(
    MAX_ZOOM,
    Math.max(MIN_ZOOM, Math.min(canvasWidth / (width * margin), canvasHeight / (height * margin))),
  );

  const centreX = (bounds.minX + bounds.maxX) / 2;
  const centreY = (bounds.minY + bounds.maxY) / 2;
  return {
    zoom,
    originX: centreX - canvasWidth / (2 * zoom),
    originY: centreY - canvasHeight / (2 * zoom),
  };
}

/**
 * Snaps a direction to the nearest 45 degrees, keeping its length. Held Shift
 * uses this: most walls in a house are orthogonal, and a hand-traced one never
 * quite is.
 */
export function orthoSnap(from: Point, to: Point): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 1) return to;

  const step = Math.PI / 4;
  const angle = Math.round(Math.atan2(dy, dx) / step) * step;
  return {
    x: from.x + Math.cos(angle) * distance,
    y: from.y + Math.sin(angle) * distance,
  };
}

/** Rounds to a grid, in millimetres. */
export function gridSnap(point: Point, gridMm: number): Point {
  if (gridMm <= 0) return point;
  return {
    x: Math.round(point.x / gridMm) * gridMm,
    y: Math.round(point.y / gridMm) * gridMm,
  };
}
